import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Character, Element, Pillar, User, ApiResponse, LoginCredentials } from '../types';

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1',
  timeout: 60000, // Increased from 30000 to 60000 (60 seconds)
  withCredentials: true, // Important for JWT cookies
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    console.log('🚀 API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`
    });
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('❌ API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data,
      message: error.message
    });
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Don't redirect automatically, let components handle it
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<{ user: User; token: string }> => {
    const response: AxiosResponse<{ status: string; token: string; data: { user: User } }> = await api.post('/users/login', credentials);
    
    // Extract token from the correct location
    const token = response.data.token;
    const user = response.data.data?.user;
    
    if (!token) {
      throw new Error('No token received from server');
    }
    
    if (!user) {
      throw new Error('No user data received from server');
    }
    
    return { user, token };
  },

  logout: async (): Promise<void> => {
    await api.post('/users/logout');
  },

  getCurrentUser: async (): Promise<User> => {
    const response: AxiosResponse<ApiResponse<User | { user: User }>> = await api.get('/users/me');
    // Handle both possible response structures
    const data = response.data.data;
    if (data && typeof data === 'object' && 'user' in data) {
      return (data as { user: User }).user;
    }
    return data as User;
  },
};

// Characters API
export const charactersAPI = {
  getAll: async (filters?: Record<string, any>): Promise<Character[]> => {
    try {
      const response: AxiosResponse<ApiResponse<{ characters: Character[] }>> = await api.get('/characters', { params: filters });
      console.log('Characters API response:', response.data);
      return response.data.data?.characters || [];
    } catch (error) {
      console.error('Characters API error:', error);
      return [];
    }
  },

  getBySlug: async (slug: string): Promise<Character> => {
    const response: AxiosResponse<ApiResponse<{ character: Character }>> = await api.get(`/characters/${slug}`);
    return response.data.data?.character!;
  },

  create: async (characterData: Partial<Character>): Promise<Character> => {
    const response: AxiosResponse<ApiResponse<{ character: Character }>> = await api.post('/characters', characterData);
    return response.data.data?.character!;
  },

  createWithImage: async (formData: FormData): Promise<Character> => {
    const response: AxiosResponse<ApiResponse<{ doc: Character }>> = await api.post('/characters', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data?.doc!;
  },

  update: async (id: string, characterData: Partial<Character>): Promise<Character> => {
    const response: AxiosResponse<ApiResponse<{ doc: Character }>> = await api.patch(`/characters/${id}`, characterData);
    return response.data.data?.doc!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/characters/${id}`);
  },
};

// Elements API
export const elementsAPI = {
  getAll: async (filters?: Record<string, any>): Promise<Element[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Element[]>> = await api.get('/elements', { params: filters });
      console.log('Elements API response:', response.data);
      return response.data.data || [];
    } catch (error) {
      console.error('Elements API error:', error);
      // If the main endpoint fails, try the basic endpoint
      try {
        console.log('Trying basic elements endpoint as fallback...');
        const basicResponse: AxiosResponse<ApiResponse<Element[]>> = await api.get('/elements/basic', { params: filters });
        console.log('Basic elements API response:', basicResponse.data);
        return basicResponse.data.data || [];
      } catch (basicError) {
        console.error('Basic elements API also failed:', basicError);
        return [];
      }
    }
  },

  getAllBasic: async (filters?: Record<string, any>): Promise<Element[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Element[]>> = await api.get('/elements/basic', { params: filters });
      console.log('Basic elements API response:', response.data);
      return response.data.data || [];
    } catch (error) {
      console.error('Basic elements API error:', error);
      return [];
    }
  },

  getBySlug: async (slug: string): Promise<Element> => {
    const response: AxiosResponse<ApiResponse<Element>> = await api.get(`/elements/${slug}`);
    return response.data.data!;
  },

  create: async (elementData: Partial<Element>): Promise<Element> => {
    const response: AxiosResponse<ApiResponse<Element>> = await api.post('/elements', elementData);
    return response.data.data!;
  },

  update: async (id: string, elementData: Partial<Element>): Promise<Element> => {
    const response: AxiosResponse<ApiResponse<Element>> = await api.patch(`/elements/${id}`, elementData);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/elements/${id}`);
  },
};

// Pillars API
export const pillarsAPI = {
  getAll: async (): Promise<Pillar[]> => {
    const response: AxiosResponse<ApiResponse<Pillar[]>> = await api.get('/pillars');
    return response.data.data!;
  },
  getById: async (id: string): Promise<Pillar> => {
    const response: AxiosResponse<ApiResponse<Pillar>> = await api.get(`/pillars/${id}`);
    return response.data.data!;
  },
  create: async (data: Partial<Pillar>): Promise<Pillar> => {
    const response: AxiosResponse<ApiResponse<Pillar>> = await api.post('/pillars', data);
    return response.data.data!;
  },
  update: async (id: string, data: Partial<Pillar>): Promise<Pillar> => {
    const response: AxiosResponse<ApiResponse<{ doc: Pillar }>> = await api.patch(`/pillars/${id}`, data);
    return response.data.data?.doc!;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/pillars/${id}`);
  },
  // Utility function to update all L1 pillar images
  updateAllL1Images: async (): Promise<{ totalUpdated: number; results: any[] }> => {
    const response: AxiosResponse<ApiResponse<{ totalUpdated: number; results: any[] }>> = await api.post('/pillars/update-l1-images');
    return response.data.data!;
  },
  // Get full hierarchy for an L1 pillar
  getHierarchy: async (l1Name: string): Promise<{ l1Pillar: any; hierarchy: any[]; totalPillars: number }> => {
    const response: AxiosResponse<ApiResponse<{ l1Pillar: any; hierarchy: any[]; totalPillars: number }>> = await api.get(`/pillars/hierarchy/${l1Name}`);
    return response.data.data!;
  }
};

// Image upload API
export const uploadAPI = {
  uploadImage: async (file: File, type: 'characters' | 'elements' | 'pillars'): Promise<{ imageUrl: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const response: AxiosResponse<ApiResponse<{ imageUrl: string }>> = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data!;
  },
};

export default api; 